import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import InboxErrorBoundary from '../components/InboxErrorBoundary';
import AiErrorBoundary from '../components/AiErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { INBOX_URL, inboxFetch, inboxSocketAuth } from '../lib/inboxApi';
import InboxAnalytics from './InboxAnalytics';
import InboxAutoReply from './InboxAutoReply';
import InboxAiSettings from './InboxAiSettings';
import InboxNotifications from './InboxNotifications';
import InboxCandidResponses from './InboxCandidResponses';
import { colors } from '../theme/tokens';

// Downscale an image File so it fits within Meta's 5 MB WhatsApp image limit
// (and so slow mobile edges don't time out mid-upload). Modern phones easily
// produce 8-12 MB JPEGs that would be rejected either by Meta directly or by
// our 60s per-attempt upload timeout. We cap at ~2000px on the longest edge
// and 85% JPEG quality which keeps photos visibly indistinguishable from the
// original on a phone screen while usually landing under 1 MB.
//
// Non-image files and already-small images (< 1.5 MB) are returned as-is so
// we never recompress a PDF, video, or an already-optimised image.
async function maybeResizeImage(file) {
  if (!file.type.startsWith('image/')) return file;
  // Meta can't display SVG/GIF as a static image after re-encoding, so pass
  // those through — Meta's own limit is 100 KB for stickers / 5 MB for other
  // images, both much more generous than what SVGs/GIFs usually weigh.
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  const SMALL_ENOUGH = 1.5 * 1024 * 1024;
  if (file.size < SMALL_ENOUGH) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const MAX_EDGE = 2000;
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85)
    );
    if (!blob) return file;
    // Replace extension with .jpg since we re-encoded as JPEG
    const baseName = (file.name || 'photo').replace(/\.(png|webp|heic|heif|bmp|tiff?|jpe?g)$/i, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (err) {
    // If anything goes wrong (unsupported codec, canvas tainted, etc.) just
    // send the original — the server-side nginx limit is 25 MB so it'll
    // probably still work, and Meta will give us a clear error if not.
    console.warn('[maybeResizeImage] fallback to original:', err);
    return file;
  }
}

// ─── Conversation List ────────────────────────────────────────────────────────
function ConversationList({
  conversations, selectedId, onSelect, loading, loadingMore, hasMore, onLoadMore,
  statusFilter, onStatusFilter,
  isRubber, settingsOpen, onToggleSettings, activePanel, onSetPanel,
  isMobile, isOpen, onClose, aiPendingCount
}) {
  const statusColors = { open: '#ef4444', pending: '#f59e0b', resolved: '#10b981' };
  const statusLabels = { open: 'Open', pending: 'Pending', resolved: 'Resolved' };

  // Infinite scroll sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting && hasMore && !loadingMore) onLoadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  const sidebarStyle = isMobile ? {
    ...s.sidebar,
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    zIndex: 200,
    transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.25s ease',
    boxShadow: isOpen ? '4px 0 20px rgba(0,0,0,0.25)' : 'none'
  } : s.sidebar;

  return (
    <div style={sidebarStyle}>
      <div style={s.sidebarHeader}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={s.sidebarTitle}>💬 Conversations</span>
          {isMobile && (
            <button onClick={onClose} style={s.sidebarCloseBtn}>✕</button>
          )}
        </div>
        <div style={s.statusFilters}>
          {[
            { key: 'active', label: 'Active' },
            { key: 'open', label: 'Open' },
            { key: 'pending', label: 'Pending' },
            { key: 'resolved', label: 'Resolved' },
            { key: 'all', label: 'All' }
          ].map(({ key, label }) => (
            <button
              key={key}
              style={{ ...s.filterBtn, ...(statusFilter === key ? s.filterBtnActive : {}) }}
              onClick={() => onStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>


      {loading && <div style={s.loadingText}>Loading conversations…</div>}
      {!loading && conversations.length === 0 && (
        <div style={s.emptyState}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
          <div>No conversations yet</div>
          <div style={{ fontSize: '12px', marginTop: '4px', color: colors.textFaint }}>
            Messages from WhatsApp will appear here
          </div>
        </div>
      )}

      <div style={s.conversationList}>
        {conversations.map(conv => (
          <div
            key={conv._id}
            style={{
              ...s.convItem,
              ...(selectedId === conv._id ? s.convItemActive : {})
            }}
            onClick={() => onSelect(conv)}
          >
            <div style={s.convAvatar}>
              {(conv.contactName || '?')[0].toUpperCase()}
            </div>
            <div style={s.convInfo}>
              <div style={s.convTop}>
                <span style={s.convName}>{conv.contactName || conv.phone}</span>
                <span style={s.convTime}>{formatTime(conv.lastMessageAt)}</span>
              </div>
              <div style={s.convBottom}>
                <span style={s.convLastMsg}>{conv.lastMessage || 'No messages yet'}</span>
                {conv.unreadCount > 0 && (
                  <span style={s.unreadBadge}>{conv.unreadCount}</span>
                )}
              </div>
              <div style={s.convMeta}>
                <span style={{
                  ...s.statusDot,
                  background: statusColors[conv.status] || '#ccc'
                }}>
                  {statusLabels[conv.status] || conv.status}
                </span>
                {conv.assignedAgent && (
                  <span style={s.assigneeBadge}>{conv.assignedAgent}</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} style={{ height: '1px' }} />
        {loadingMore && (
          <div style={{ padding: '12px', textAlign: 'center', color: colors.textMuted, fontSize: '12px' }}>
            Loading more…
          </div>
        )}
      </div>

      <div style={s.sidebarFooter}>
          <button
            style={{ ...s.gearBtn, ...(settingsOpen ? s.gearBtnActive : {}) }}
            onClick={onToggleSettings}
            title="Settings"
          >
            ⚙️ Settings
          </button>
          {settingsOpen && (
            <div style={s.settingsNav}>
              <button
                style={{ ...s.settingsNavBtn, ...(activePanel === 'analytics' ? s.settingsNavBtnActive : {}) }}
                onClick={() => onSetPanel('analytics')}
              >
                📊 Analytics
              </button>
              {isRubber && (
                <button
                  style={{ ...s.settingsNavBtn, ...(activePanel === 'autoreply' ? s.settingsNavBtnActive : {}) }}
                  onClick={() => onSetPanel('autoreply')}
                >
                  ↩️ Auto-reply
                </button>
              )}
              {isRubber && (
                <button
                  style={{ ...s.settingsNavBtn, color: colors.textFaint, cursor: 'default' }}
                  disabled
                >
                  🔀 Flow Builder (soon)
                </button>
              )}
              {isRubber && (
                <button
                  style={{ ...s.settingsNavBtn, ...(activePanel === 'ai' ? s.settingsNavBtnActive : {}), display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => onSetPanel('ai')}
                >
                  <span>🤖 AI Reply</span>
                  {aiPendingCount > 0 && (
                    <span style={s.aiBadge}>{aiPendingCount}</span>
                  )}
                </button>
              )}
              <button
                style={{ ...s.settingsNavBtn, ...(activePanel === 'candid' ? s.settingsNavBtnActive : {}) }}
                onClick={() => onSetPanel('candid')}
              >
                💬 Candid Responses
              </button>
              <button
                style={{ ...s.settingsNavBtn, ...(activePanel === 'notifications' ? s.settingsNavBtnActive : {}) }}
                onClick={() => onSetPanel('notifications')}
              >
                🔔 Notifications
              </button>
            </div>
          )}
        </div>
    </div>
  );
}

// ─── Message Content ─────────────────────────────────────────────────────────
function resolveMediaUrl(url) {
  if (!url) return url;
  if (url.startsWith('/uploads/')) return `${INBOX_URL}${url}`;
  return url;
}

function MessageContent({ msg }) {
  const isOutbound = msg.direction === 'outbound';

  if (msg.mediaUrl) {
    const type = msg.type;
    const mediaUrl = resolveMediaUrl(msg.mediaUrl);

    // Caption typed alongside the media (either by the customer on WhatsApp
    // or the agent in the attachment preview). Rendered under the media just
    // like WhatsApp does. Linkified so pasted URLs stay clickable.
    const captionBlock = msg.caption ? (
      <div style={{
        marginTop: '6px',
        fontSize: '13px',
        lineHeight: 1.4,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap'
      }}>
        {linkifyText(msg.caption, isOutbound)}
      </div>
    ) : null;

    if (type === 'image' || type === 'sticker') {
      return (
        <>
          <a href={mediaUrl} target="_blank" rel="noreferrer">
            <img
              src={mediaUrl}
              alt={type}
              style={{
                maxWidth: '100%', maxHeight: '260px',
                borderRadius: '8px', display: 'block',
                cursor: 'pointer'
              }}
            />
          </a>
          {captionBlock}
        </>
      );
    }
    if (type === 'audio') {
      // Audio can't have a caption in WhatsApp's API, but if one ever slips
      // through (legacy data, manual DB edit) still show it.
      return (
        <>
          <audio
            controls
            src={mediaUrl}
            style={{ width: '100%', minWidth: '200px', display: 'block', marginTop: '2px' }}
          />
          {captionBlock}
        </>
      );
    }
    if (type === 'video') {
      return (
        <>
          <video
            controls
            src={mediaUrl}
            style={{
              maxWidth: '100%', maxHeight: '260px',
              borderRadius: '8px', display: 'block'
            }}
          />
          {captionBlock}
        </>
      );
    }
    if (type === 'document') {
      return (
        <>
          <a
            href={mediaUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              color: isOutbound ? 'rgba(255,255,255,0.9)' : colors.blue,
              textDecoration: 'none', fontWeight: '600', fontSize: '13px'
            }}
          >
            📄 {msg.body || 'Document'}
          </a>
          {captionBlock}
        </>
      );
    }
  }

  // Plain text — detect and linkify http/https URLs, skip downloadable file links
  return (
    <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
      {linkifyText(msg.body, isOutbound)}
    </div>
  );
}

// ─── Chat Panel ───────────────────────────────────────────────────────────────
function ChatPanel({ conversation, messages, onSendMessage, onSendNote, onSendMedia, sending, isMobile, onToggleLeft, onToggleRight, isOutside24h, aiSuggestion, aiType, aiLoading, aiError, onDismissAi, onRefreshAi, aiGloballyEnabled, onToggleChatAi, candidResponses = [] }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('reply'); // 'reply' | 'note'
  const [attachment, setAttachment] = useState(null); // { file, previewUrl, mimeType }
  const [sendingMedia, setSendingMedia] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const bottomRef = useRef(null);

  // Candid-response dropdown. Opens upward from the reply button, closes
  // on outside click or after a template is inserted.
  const [showCandid, setShowCandid] = useState(false);
  const candidRef = useRef(null);
  useEffect(() => {
    if (!showCandid) return;
    const onDocClick = (e) => {
      if (candidRef.current && !candidRef.current.contains(e.target)) {
        setShowCandid(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showCandid]);

  // Insert a template into the input. If the field is empty, use the body
  // as-is; if the agent already had a draft, append with a blank line so
  // nothing they typed gets clobbered.
  const insertCandid = (body) => {
    setText(prev => {
      const clean = (prev || '').replace(/\s+$/, '');
      return clean ? `${clean}\n\n${body}` : body;
    });
    setShowCandid(false);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clean up preview URL on unmount or change
  useEffect(() => {
    return () => { if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl); };
  }, [attachment]);

  const handleSend = () => {
    if (!text.trim() || sending) return;
    if (mode === 'note') {
      onSendNote(text.trim());
    } else {
      onSendMessage(text.trim());
    }
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const acceptFile = (file) => {
    if (!file) return;
    const MAX_MB = 15;
    if (file.size > MAX_MB * 1024 * 1024) {
      alert(`File too large. Max ${MAX_MB}MB.`);
      return;
    }
    const name = (file.name || '').toLowerCase();
    let mime = file.type || '';
    // Some browsers don't set a mime type for .pdf drops — fall back to extension
    if (!mime && name.endsWith('.pdf')) mime = 'application/pdf';

    const isAllowed =
      mime.startsWith('image/') ||
      mime.startsWith('video/') ||
      mime.startsWith('audio/') ||
      mime === 'application/pdf';
    if (!isAllowed) {
      alert('Only image, video, audio or PDF files can be attached.');
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setAttachment({ file, previewUrl, mimeType: mime });
  };

  const handleFileChange = (e) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = ''; // reset so same file can be reselected
  };

  // Drag-and-drop onto the chat panel → stage as attachment.
  // Disabled while outside the 24h window in reply mode, during send, or in note mode.
  const dropDisabled = (isOutside24h && mode === 'reply') || mode === 'note' || sending || sendingMedia;

  const handleDragEnter = (e) => {
    if (dropDisabled) return;
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragActive(true);
  };
  const handleDragOver = (e) => {
    if (dropDisabled) return;
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = (e) => {
    if (dropDisabled) return;
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragActive(false);
  };
  const handleDrop = (e) => {
    if (dropDisabled) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    acceptFile(file);
  };

  // Ctrl+V / Cmd+V on the chat textarea — pulls an image/video/audio/PDF from
  // the clipboard (screenshots, copied web images, copied files) and stages
  // it as the attachment. Plain text paste is left untouched.
  const handlePaste = (e) => {
    if (dropDisabled) return;
    const items = e.clipboardData?.items;
    if (!items?.length) return;

    for (const item of items) {
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (!file) continue;

      // Screenshots from Snipping Tool / Print Screen arrive with a generic
      // name like "image.png" (or sometimes nothing). Re-stamp them so the
      // saved attachment has a unique, sortable filename.
      const looksGeneric = !file.name || /^image\.(png|jpg|jpeg|webp|gif)$/i.test(file.name);
      let staged = file;
      if (looksGeneric) {
        const ext = (file.type.split('/')[1] || 'png').toLowerCase();
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        staged = new File([file], `paste-${stamp}.${ext}`, { type: file.type });
      }

      e.preventDefault();
      acceptFile(staged);
      return; // first file wins, same behaviour as drag-and-drop
    }
  };

  // Caption typed alongside an attachment (WhatsApp-style "type with your
  // photo"). Only meaningful for image/video/document — the caption input is
  // hidden for audio, which Meta doesn't caption anyway.
  const [caption, setCaption] = useState('');
  const captionInputRef = useRef(null);

  // When an attachment is staged, focus the caption input so agents can type
  // immediately — same UX as WhatsApp's "add caption" screen.
  useEffect(() => {
    if (attachment && captionInputRef.current) {
      captionInputRef.current.focus();
    }
  }, [attachment]);

  const handleSendMedia = async () => {
    if (!attachment || sendingMedia) return;
    setSendingMedia(true);
    try {
      // Downscale big phone photos BEFORE base64-encoding — otherwise a 10 MB
      // JPEG becomes a ~13 MB base64 payload that Meta will reject (5 MB
      // image limit) and that may time out mid-upload. For non-images and
      // small images this is a cheap no-op.
      const fileToSend = await maybeResizeImage(attachment.file);
      const sendMime = fileToSend.type || attachment.mimeType;
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result; // includes data URI prefix
        await onSendMedia({
          base64,
          mimeType: sendMime,
          filename: fileToSend.name,
          caption: caption.trim()
        });
        setAttachment(null);
        setCaption('');
        setSendingMedia(false);
      };
      reader.onerror = () => {
        alert('Could not read file. Please try again.');
        setSendingMedia(false);
      };
      reader.readAsDataURL(fileToSend);
    } catch {
      setSendingMedia(false);
    }
  };

  const handleCaptionKeyDown = (e) => {
    // Enter sends, Shift+Enter inserts a newline — mirrors the reply textarea.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMedia();
    }
  };

  if (!conversation) {
    return (
      <div style={s.chatEmpty}>
        {isMobile && (
          <button onClick={onToggleLeft} style={s.mobileMenuBtn}>☰ Conversations</button>
        )}
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>💬</div>
        <div style={{ color: colors.textMuted, fontSize: '16px' }}>Select a conversation to start</div>
      </div>
    );
  }

  return (
    <div
      style={{ ...s.chatPanel, position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragActive && (
        <div style={s.dropOverlay}>
          <div style={s.dropOverlayInner}>
            <div style={s.dropIcon}>📎</div>
            <div style={s.dropTitle}>Drop to attach</div>
            <div style={s.dropHint}>Image, video, audio or PDF (max 15 MB)</div>
          </div>
        </div>
      )}
      <div style={s.chatHeader}>
        {isMobile && (
          <button onClick={onToggleLeft} style={s.mobileIconBtn} title="Conversations">☰</button>
        )}
        <div style={s.chatAvatar}>
          {(conversation.contactName || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.chatName}>{conversation.contactName || conversation.phone}</div>
          <div style={s.chatPhone}>{conversation.phone}</div>
        </div>
        {/* Per-chat AI toggle — greyed out if AI isn't globally enabled */}
        {(() => {
          const chatAiOn = aiGloballyEnabled && !conversation.aiDisabled;
          const tooltip = !aiGloballyEnabled
            ? 'AI is globally disabled — enable it in Inbox → AI Settings'
            : chatAiOn
            ? 'AI suggestions ON for this chat — click to disable'
            : 'AI suggestions OFF for this chat — click to enable';
          return (
            <button
              onClick={aiGloballyEnabled ? onToggleChatAi : undefined}
              style={{
                ...s.chatAiToggle,
                ...(chatAiOn ? s.chatAiToggleOn : s.chatAiToggleOff),
                ...(aiGloballyEnabled ? {} : s.chatAiToggleDisabled)
              }}
              title={tooltip}
              aria-pressed={chatAiOn}
            >
              <span style={s.chatAiIcon}>{chatAiOn ? '🤖' : '🚫'}</span>
              <span style={s.chatAiLabel}>{chatAiOn ? 'AI' : 'AI off'}</span>
            </button>
          );
        })()}
        {isMobile && (
          <button onClick={onToggleRight} style={s.mobileIconBtn} title="Contact Info">ℹ</button>
        )}
      </div>

      <div style={s.messagesArea}>
        {messages.length === 0 && (
          <div style={s.noMessages}>No messages yet in this conversation</div>
        )}
        {messages.map(msg => {
          const isNote = msg.type === 'note';
          const isOutbound = msg.direction === 'outbound';
          // AI-authored outbound messages get a blue bubble so agents can tell
          // at a glance whether a reply came from a human or from an AI-assist click.
          const bubbleTone = isNote
            ? s.noteBubble
            : isOutbound
              ? (msg.isAi ? s.outboundAi : s.outbound)
              : s.inbound;
          return (
            <div
              key={msg._id}
              style={{ ...s.messageBubble, ...bubbleTone }}
            >
              {isNote && (
                <div style={s.noteLabel}>Internal Note — {msg.agentName || 'Agent'}</div>
              )}
              {!isNote && isOutbound && msg.agentName && (
                <div style={s.agentLabel}>
                  {msg.isAi ? `🤖 AI · ${msg.agentName}` : msg.agentName}
                </div>
              )}
              <MessageContent msg={msg} />
              <div style={s.messageTime}>{formatTime(msg.createdAt)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* AI Suggestion Bar — sits above the message input */}
      <AiErrorBoundary inline>
        {aiLoading && (
          <div style={s.aiBar}>
            <span style={s.aiBarIcon}>🤖</span>
            <span style={{ ...s.aiBarText, color: colors.textMuted, fontStyle: 'italic' }}>
              AI is assessing the conversation…
            </span>
          </div>
        )}
        {!aiLoading && !aiSuggestion && aiError && (
          <div style={{ ...s.aiBar, ...s.aiBarError }}>
            <span style={s.aiBarIcon}>⚠️</span>
            <div style={s.aiBarBody}>
              <div style={{ ...s.aiBarLabel, color: '#f3b0aa' }}>AI unavailable</div>
              <div style={s.aiBarText}>{aiError}</div>
              <div style={s.aiBarActions}>
                <button style={s.aiRefreshBtn} onClick={onRefreshAi}>↺ Retry</button>
              </div>
            </div>
            <button style={s.aiDismissBtn} onClick={onDismissAi} title="Dismiss">✕</button>
          </div>
        )}
        {!aiLoading && aiSuggestion && (
          <div style={{
            ...s.aiBar,
            ...(aiType === 'priming' ? s.aiBarPriming : s.aiBarRecommendation)
          }}>
            <span style={s.aiBarIcon}>
              {aiType === 'priming' ? '💬' : '🤖'}
            </span>
            <div style={s.aiBarBody}>
              <div style={{
                ...s.aiBarLabel,
                ...(aiType === 'priming' ? s.aiBarLabelPriming : s.aiBarLabelRecommendation)
              }}>
                {aiType === 'priming' ? 'Suggested Question' : 'AI Suggestion'}
              </div>
              <div style={s.aiBarText}>{aiSuggestion}</div>
              <div style={s.aiBarActions}>
                <button
                  style={{
                    ...s.aiUseBtn,
                    ...(aiType === 'priming' ? s.aiUseBtnPriming : {}),
                    ...(sending ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                  }}
                  disabled={sending}
                  onClick={() => {
                    if (sending) return;
                    const toSend = aiSuggestion;
                    onDismissAi();
                    onSendMessage(toSend, { isAi: true });
                  }}
                >
                  {aiType === 'priming' ? 'Send this question' : 'Send this reply'}
                </button>
                <button style={s.aiRefreshBtn} onClick={onRefreshAi} title="Get a fresh suggestion">
                  ↺ Refresh
                </button>
              </div>
            </div>
            <button style={s.aiDismissBtn} onClick={onDismissAi} title="Dismiss">✕</button>
          </div>
        )}
      </AiErrorBoundary>

      <div style={s.inputWrapper}>
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,application/pdf,.pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {/* Attachment preview — WhatsApp-style: shows the thumbnail on top and
            a caption input + send button below so agents can type alongside
            the media before sending. */}
        {attachment && (() => {
          // Meta accepts captions on image/video/document only. For audio (voice
          // notes / recorded clips) we hide the caption input so agents don't
          // waste time typing something that would be silently dropped.
          const captionable =
            attachment.mimeType.startsWith('image/') ||
            attachment.mimeType.startsWith('video/') ||
            attachment.mimeType === 'application/pdf';
          return (
            <div style={s.attachPreviewWrap}>
              <div style={s.attachPreviewBar}>
                {attachment.mimeType.startsWith('image/') ? (
                  <img src={attachment.previewUrl} alt="preview" style={s.attachThumb} />
                ) : (
                  <div style={s.attachFileIcon}>
                    {attachment.mimeType === 'application/pdf'
                      ? '📄'
                      : attachment.mimeType.startsWith('video/')
                      ? '🎥'
                      : '🎙️'}
                    <span style={{ fontSize: '11px', marginLeft: '4px' }}>{attachment.file.name}</span>
                  </div>
                )}
                <div style={{ flex: 1, fontSize: '11px', color: colors.textMuted, paddingLeft: '6px' }}>
                  {(attachment.file.size / 1024 / 1024).toFixed(1)} MB
                </div>
                <button
                  style={s.attachCancelBtn}
                  onClick={() => { setAttachment(null); setCaption(''); }}
                >
                  ✕
                </button>
              </div>

              <div style={s.captionRow}>
                {captionable ? (
                  <textarea
                    ref={captionInputRef}
                    style={s.captionInput}
                    rows={2}
                    maxLength={1024}
                    placeholder="Add a caption… (Enter to send, Shift+Enter for new line)"
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    onKeyDown={handleCaptionKeyDown}
                    disabled={sendingMedia}
                  />
                ) : (
                  <div style={s.captionUnsupported}>
                    Captions aren't supported for audio attachments.
                  </div>
                )}
                <button
                  style={{
                    ...s.sendBtn,
                    ...(sendingMedia ? s.sendBtnDisabled : {}),
                    flexShrink: 0
                  }}
                  onClick={handleSendMedia}
                  disabled={sendingMedia}
                  title="Send"
                >
                  {sendingMedia ? '…' : '➤'}
                </button>
              </div>
            </div>
          );
        })()}

        {!attachment && (isOutside24h && mode === 'reply' ? (
          <div style={s.windowBlockedBar}>
            ⛔ Outside 24h window — you can only send template messages or internal notes
          </div>
        ) : (
          <div style={s.modeToggle}>
            <button
              style={{ ...s.modeBtn, ...(mode === 'reply' ? s.modeBtnActive : {}) }}
              onClick={() => setMode('reply')}
            >
              ➤ Reply
            </button>
            <button
              style={{ ...s.modeBtn, ...(mode === 'note' ? s.modeNoteActive : {}) }}
              onClick={() => setMode('note')}
            >
              📝 Note
            </button>

            {/* Candid-response dropdown — team-wide shared templates. */}
            <div ref={candidRef} style={s.candidWrap}>
              <button
                style={{ ...s.modeBtn, ...(showCandid ? s.modeBtnActive : {}) }}
                onClick={() => setShowCandid(v => !v)}
                title="Insert a saved candid response"
              >
                💬 Candid ▾
              </button>
              {showCandid && (
                <div style={s.candidMenu}>
                  {candidResponses.length === 0 ? (
                    <div style={s.candidEmpty}>
                      No candid responses yet.<br />
                      Add some in Settings → Candid Responses.
                    </div>
                  ) : (
                    candidResponses.map(item => (
                      <button
                        key={item._id}
                        style={s.candidItem}
                        onClick={() => insertCandid(item.body)}
                        title={item.body}
                      >
                        <span style={s.candidItemTitle}>{item.title}</span>
                        <span style={s.candidItemPreview}>{item.body}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {!attachment && (
          <div style={{
            ...s.inputRow,
            ...(mode === 'note' ? s.inputRowNote : {}),
            ...(isOutside24h && mode === 'reply' ? s.inputRowBlocked : {})
          }}>
            {/* Attachment buttons — only in reply mode, only within 24h window */}
            {mode === 'reply' && !isOutside24h && (
              <>
                <button
                  style={s.attachBtn}
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach from gallery"
                >
                  📎
                </button>
                <button
                  style={s.attachBtn}
                  onClick={() => cameraInputRef.current?.click()}
                  title="Take photo or video"
                >
                  📷
                </button>
              </>
            )}
            <textarea
              style={{
                ...s.textarea,
                ...(isOutside24h && mode === 'reply' ? s.textareaBlocked : {})
              }}
              rows={2}
              placeholder={
                isOutside24h && mode === 'reply'
                  ? 'Outside 24h window — switch to Note or use a template'
                  : mode === 'note'
                  ? 'Add an internal note… (not sent to customer)'
                  : 'Type a message… (Enter to send, Shift+Enter for new line)'
              }
              value={isOutside24h && mode === 'reply' ? '' : text}
              onChange={e => { if (!(isOutside24h && mode === 'reply')) setText(e.target.value); }}
              onKeyDown={isOutside24h && mode === 'reply' ? undefined : handleKeyDown}
              onPaste={isOutside24h && mode === 'reply' ? undefined : handlePaste}
              disabled={sending || (isOutside24h && mode === 'reply')}
            />
            <button
              style={{
                ...s.sendBtn,
                ...(mode === 'note' ? s.sendBtnNote : {}),
                ...(sending || !text.trim() || (isOutside24h && mode === 'reply') ? s.sendBtnDisabled : {})
              }}
              onClick={isOutside24h && mode === 'reply' ? undefined : handleSend}
              disabled={sending || !text.trim() || (isOutside24h && mode === 'reply')}
            >
              {sending ? '…' : mode === 'note' ? '📝' : '➤'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Contact Detail ───────────────────────────────────────────────────────────
function ContactDetail({
  conversation, onUpdateStatus, onUpdateAssignee,
  isMobile, isOpen, onClose,
  inboxAgents, windowInfo,
  isRubber, onWipeChat
}) {
  const [assigneeInput, setAssigneeInput] = useState('');

  useEffect(() => {
    setAssigneeInput(conversation?.assignedAgent || '');
  }, [conversation?._id, conversation?.assignedAgent]);

  const panelStyle = isMobile ? {
    ...s.detailPanel,
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    zIndex: 200,
    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 0.25s ease',
    boxShadow: isOpen ? '-4px 0 20px rgba(0,0,0,0.25)' : 'none'
  } : s.detailPanel;

  if (!conversation) {
    if (isMobile) return null;
    return <div style={s.detailEmpty} />;
  }

  const statuses = ['open', 'pending', 'resolved'];

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={s.detailHeader}>Contact Info</div>
        {isMobile && (
          <button onClick={onClose} style={s.sidebarCloseBtn}>✕</button>
        )}
      </div>
      <div style={s.detailAvatar}>
        {(conversation.contactName || '?')[0].toUpperCase()}
      </div>
      <div style={s.detailName}>{conversation.contactName || 'Unknown'}</div>
      <div style={s.detailPhone}>{conversation.phone}</div>

      <div style={s.detailDivider} />

      {/* 24-hour messaging window */}
      <div style={s.detailLabel}>24h Messaging Window</div>
      {windowInfo ? (
        <div style={{
          ...s.windowBadge,
          ...(windowInfo.outside ? s.windowBadgeExpired : s.windowBadgeActive)
        }}>
          {windowInfo.outside
            ? '⛔ Outside window — template only'
            : `✅ ${windowInfo.label} remaining`}
        </div>
      ) : (
        <div style={{ ...s.windowBadge, background: colors.surface, color: colors.textMuted }}>
          — No inbound messages yet
        </div>
      )}

      <div style={s.detailDivider} />

      <div style={s.detailLabel}>Status</div>
      <div style={s.statusButtons}>
        {statuses.map(st => (
          <button
            key={st}
            style={{
              ...s.statusBtn,
              ...(conversation.status === st ? s.statusBtnActive : {})
            }}
            onClick={() => onUpdateStatus(st)}
          >
            {st.charAt(0).toUpperCase() + st.slice(1)}
          </button>
        ))}
      </div>

      <div style={s.detailDivider} />
      <div style={s.detailLabel}>Assigned to</div>
      <select
        style={s.assigneeSelect}
        value={assigneeInput}
        onChange={e => {
          setAssigneeInput(e.target.value);
          onUpdateAssignee(e.target.value);
        }}
      >
        <option value="">— Unassigned —</option>
        {(inboxAgents || []).map(a => (
          <option key={a._id} value={a.name.split(' ')[0]}>
            {a.name}
          </option>
        ))}
      </select>

      <div style={s.detailDivider} />
      <div style={s.detailLabel}>Last message</div>
      <div style={s.detailValue}>{formatTime(conversation.lastMessageAt)}</div>

      {/* Destructive action — wipes ONLY this conversation + its messages +
          attached media files. Rubber-tier only. Lives at the very bottom of
          the contact-info panel, visually separated, so it's hard to hit by
          mistake and clearly scoped to the currently-open chat. */}
      {isRubber && (
        <>
          <div style={s.detailDivider} />
          <div style={s.wipeZone}>
            <div style={s.wipeZoneLabel}>Danger zone</div>
            <button
              style={s.wipeBtn}
              onClick={onWipeChat}
              title={`Permanently delete this chat with ${conversation.contactName || conversation.phone} (Rubber-tier only)`}
            >
              🗑️ Wipe this chat
            </button>
            <div style={s.wipeZoneHint}>
              Deletes this conversation, its messages and any attached media.
              Other chats are not affected.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Settings Panel ───────────────────────────────────────────────────────────
function SettingsPanel({ activePanel, onClose, onAiPendingCountChange, user }) {
  const titles = {
    analytics: '📊 Analytics',
    autoreply: '↩️ Auto-reply Settings',
    ai: '🤖 AI Reply',
    candid: '💬 Candid Responses',
    notifications: '🔔 Notifications'
  };
  return (
    <div style={s.settingsPanelWrap}>
      <div style={s.settingsPanelHeader}>
        <span style={s.settingsPanelTitle}>{titles[activePanel] || ''}</span>
        <button style={s.settingsCloseBtn} onClick={onClose}>✕ Close</button>
      </div>
      <div style={s.settingsPanelBody}>
        {activePanel === 'analytics' && <InboxAnalytics />}
        {activePanel === 'autoreply' && <InboxAutoReply />}
        {activePanel === 'ai' && (
          <InboxAiSettings onPendingCountChange={onAiPendingCountChange} />
        )}
        {activePanel === 'candid' && <InboxCandidResponses />}
        {activePanel === 'notifications' && (
          <InboxNotifications agentEmail={user?.email} agentName={user?.name} />
        )}
      </div>
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Format ms remaining as "Xh Ym"
function formatTimeLeft(ms) {
  if (ms <= 0) return '0m';
  const totalMins = Math.floor(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─── Main SupportInbox (wrapped in error boundary) ────────────────────────────
function SupportInboxInner() {
  const { user, isRubber, token } = useAuth();
  const location = useLocation();
  const agentFirstName = user?.name?.split(' ')[0] || 'Agent';

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [error, setError] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // 'analytics' | 'autoreply' | 'ai' | null
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [inboxAgents, setInboxAgents] = useState([]);
  const [, setTick] = useState(0); // forces re-render every minute for 24h countdown
  const socketRef = useRef(null);
  // Ref tracks which conversation is open so socket handler never reads stale state
  const selectedConvIdRef = useRef(null);

  // AI suggestion state
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiType, setAiType] = useState('recommendation'); // 'priming' | 'recommendation'
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiPendingCount, setAiPendingCount] = useState(0);
  const aiDebounceRef = useRef(null);

  // Candid responses — team-wide shared library shown in the chat dropdown.
  // Refetched whenever the settings panel is closed (or was never the candid
  // panel) so edits/deletes propagate to every agent without a page reload.
  const [candidResponses, setCandidResponses] = useState([]);

  // Detect mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // When the Inbox nav tab is tapped while already on this page, React Router
  // pushes a new history entry (new location.key). On mobile this means the
  // user wants to go back to the conversation list, so close any open chat and
  // slide the sidebar open.
  const navKeyMountedRef = useRef(false);
  useEffect(() => {
    if (!navKeyMountedRef.current) { navKeyMountedRef.current = true; return; }
    if (isMobile) {
      setSelectedConv(null);
      selectedConvIdRef.current = null;
      setActivePanel(null);
      setRightOpen(false);
      setLeftOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Tick every minute to keep 24h countdown accurate
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Keep the ref in sync with the latest selectedConv so socket handlers and
  // the AI fetch debounce can read its freshest state without re-subscribing.
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  // When AI becomes globally enabled (or the user returns to this tab) and a
  // chat is already open, kick off a suggestion so they don't have to wait
  // for the next inbound message.
  useEffect(() => {
    if (aiEnabled && selectedConv?._id && !selectedConv.aiDisabled) {
      fetchAiSuggestion(selectedConv._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiEnabled]);

  // Fetch inbox-enabled users for assignee dropdown
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/admin/inbox-agents`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setInboxAgents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  // Check if AI is enabled and load initial pending enrichment count.
  // Also re-runs whenever the AI Settings panel closes so toggling the global
  // switch is reflected immediately without a page reload.
  useEffect(() => {
    if (activePanel === 'ai') return; // defer until the user closes the panel
    inboxFetch(`/api/ai/settings`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAiEnabled(data.enabled); })
      .catch(() => {});
    inboxFetch(`/api/ai/enrichments?status=pending`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.pendingCount != null) setAiPendingCount(data.pendingCount); })
      .catch(() => {});
  }, [activePanel]);

  // Refetch candid responses whenever we're NOT actively on the candid
  // settings panel (same pattern as AI above). On mount activePanel is null
  // so this seeds the dropdown; when a user closes the settings after
  // editing, the chat immediately reflects their change.
  useEffect(() => {
    if (activePanel === 'candid') return;
    inboxFetch(`/api/candid-responses`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data)) setCandidResponses(data); })
      .catch(() => {});
  }, [activePanel]);

  // Ref so socket handlers always see the latest selected conversation object
  // (for checking aiDisabled without re-running the socket effect)
  const selectedConvRef = useRef(null);

  // Fetch AI suggestion for a conversation (debounced).
  // Skipped if global AI is off OR this specific chat has AI disabled.
  const fetchAiSuggestion = useCallback((convId) => {
    if (!aiEnabled || !convId) return;
    const conv = selectedConvRef.current;
    if (conv && conv._id === convId && conv.aiDisabled) return;
    clearTimeout(aiDebounceRef.current);
    aiDebounceRef.current = setTimeout(async () => {
      setAiLoading(true);
      setAiSuggestion('');
      setAiError('');
      try {
        const res = await inboxFetch(`/api/ai/suggest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversationId: convId })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          if (data.disabled) {
            // Per-chat AI was just turned off — nothing to show
          } else if (data.suggestion) {
            setAiSuggestion(data.suggestion);
            setAiType(data.type || 'recommendation');
          }
        } else {
          // Surface the error so failures don't hide silently like they did
          // when OPENAI_API_KEY was missing on the VPS.
          const msg = data?.error || `AI service returned ${res.status}`;
          setAiError(msg.includes('OPENAI_API_KEY')
            ? 'AI is missing an OpenAI API key on the server.'
            : msg);
        }
      } catch (err) {
        setAiError('Could not reach the AI service.');
      } finally {
        setAiLoading(false);
      }
    }, 1500);
  }, [aiEnabled]);

  // Connect to socket
  useEffect(() => {
    try {
      socketRef.current = io(INBOX_URL, {
        transports: ['websocket', 'polling'],
        auth: inboxSocketAuth()
      });

      socketRef.current.on('new_message', (data) => {
        // Update conversation list
        setConversations(prev => {
          if (data.conversation) {
            const convId = data.conversation._id?.toString() || data.conversation._id;
            const exists = prev.find(c => c._id === convId);
            if (exists) {
              return prev
                .map(c => c._id === convId ? data.conversation : c)
                .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
            }
            return [data.conversation, ...prev];
          }
          return prev;
        });

        // Add message to chat only if it belongs to the currently open conversation
        // Deduplicate by _id to prevent double-render of outbound messages
        const incomingConvId = data.conversationId?.toString?.() || String(data.conversationId);
        if (selectedConvIdRef.current && selectedConvIdRef.current === incomingConvId) {
          setMessages(msgs => {
            if (msgs.some(m => m._id === data.message._id)) return msgs;
            return [...msgs, data.message];
          });
          // Auto-fetch AI suggestion when a new inbound message arrives
          if (data.message?.direction === 'inbound') {
            fetchAiSuggestion(incomingConvId);
          }
        }
      });

      socketRef.current.on('conversation_updated', (data) => {
        if (data.conversation) {
          const convId = data.conversation._id?.toString() || data.conversation._id;
          setConversations(prev =>
            prev.map(c => c._id === convId ? data.conversation : c)
          );
          setSelectedConv(prev =>
            prev && prev._id === convId ? data.conversation : prev
          );
        }
      });

      // Rubber-tier agents can permanently wipe a chat; every connected inbox
      // must drop it from the list immediately so nobody ends up replying into
      // a deleted conversation.
      socketRef.current.on('conversation_deleted', (data) => {
        const convId = data?.conversationId?.toString() || data?.conversationId;
        if (!convId) return;
        setConversations(prev => prev.filter(c => c._id !== convId));
        setSelectedConv(prev => (prev && prev._id === convId ? null : prev));
        selectedConvIdRef.current = selectedConvIdRef.current === convId ? null : selectedConvIdRef.current;
      });

      // Update enrichment badge count in real-time
      socketRef.current.on('new_enrichment', (data) => {
        if (typeof data.pendingCount === 'number') {
          setAiPendingCount(data.pendingCount);
        }
      });
    } catch (err) {
      console.error('Socket connection failed:', err);
    }
    return () => socketRef.current?.disconnect();
  }, [fetchAiSuggestion]);

  // Load conversations — page=1 replaces list, page>1 appends (infinite scroll)
  const loadConversations = useCallback(async (pageNum = 1) => {
    try {
      if (pageNum === 1) { setLoadingConvs(true); setError(null); }
      else setLoadingMore(true);

      const params = new URLSearchParams({ page: pageNum, limit: 30 });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await inboxFetch(`/api/conversations?${params}`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      const incoming = data.conversations || [];
      if (pageNum === 1) {
        setConversations(incoming);
      } else {
        setConversations(prev => {
          const existingIds = new Set(prev.map(c => c._id));
          return [...prev, ...incoming.filter(c => !existingIds.has(c._id))];
        });
      }
      setHasMore(data.hasMore || false);
      setPage(pageNum);
    } catch (err) {
      if (pageNum === 1) setError('Could not load conversations. Check your inbox server connection.');
      console.error('Load conversations error:', err);
    } finally {
      setLoadingConvs(false);
      setLoadingMore(false);
    }
  }, [statusFilter]);

  // Reset to page 1 whenever filter changes
  useEffect(() => { loadConversations(1); }, [loadConversations]);

  // Load messages when conversation selected
  const selectConversation = async (conv) => {
    selectedConvIdRef.current = conv._id;
    selectedConvRef.current = conv;
    setSelectedConv(conv);
    setActivePanel(null);
    setAiSuggestion(''); // clear stale suggestion from previous conversation
    setAiType('recommendation');
    if (isMobile) setLeftOpen(false); // close sidebar drawer on mobile
    setLoadingMsgs(true);
    try {
      const res = await inboxFetch(`/api/messages/${conv._id}`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setMessages(data);
      await inboxFetch(`/api/conversations/${conv._id}/read`, { method: 'PATCH' });
      setConversations(prev =>
        prev.map(c => c._id === conv._id ? { ...c, unreadCount: 0 } : c)
      );
      // Fetch AI suggestion for the latest message of the newly selected conversation
      fetchAiSuggestion(conv._id);
    } catch (err) {
      console.error('Load messages error:', err);
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  // Send message — do NOT add to state here; the socket new_message event handles it (deduplicated).
  // opts.isAi marks messages that came from clicking "Use this reply" on an AI suggestion so the
  // chat UI can render them in a distinct colour.
  const sendMessage = async (text, opts = {}) => {
    if (!selectedConv) return;
    setSending(true);
    try {
      const res = await inboxFetch(`/api/messages/${selectedConv._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, agentName: agentFirstName, isAi: Boolean(opts.isAi) })
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
    } catch (err) {
      console.error('Send message error:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Send a media attachment (image/video/audio from gallery or camera).
  // `caption` is optional — WhatsApp accepts it for image/video/document only;
  // the backend guards this so we can always forward whatever the agent typed.
  const sendMedia = async ({ base64, mimeType, filename, caption }) => {
    if (!selectedConv) return;
    try {
      const res = await inboxFetch(`/api/messages/${selectedConv._id}/media`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64, mimeType, filename, caption, agentName: agentFirstName })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to send media. Please try again.');
      }
    } catch (err) {
      console.error('Send media error:', err);
      alert('Failed to send media. Please try again.');
    }
  };

  // Save internal note — same pattern: socket event handles adding to state
  const sendNote = async (text) => {
    if (!selectedConv) return;
    setSending(true);
    try {
      const res = await inboxFetch(`/api/messages/${selectedConv._id}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, agentName: agentFirstName })
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
    } catch (err) {
      console.error('Note error:', err);
      alert('Failed to save note. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Update conversation status
  const updateStatus = async (status) => {
    if (!selectedConv) return;
    try {
      const res = await inboxFetch(`/api/conversations/${selectedConv._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const updated = await res.json();
      setSelectedConv(updated);
      setConversations(prev => prev.map(c => c._id === updated._id ? updated : c));
    } catch (err) {
      console.error('Update status error:', err);
    }
  };

  // Update assigned agent
  const updateAssignee = async (agentName) => {
    if (!selectedConv) return;
    try {
      const res = await inboxFetch(`/api/conversations/${selectedConv._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedAgent: agentName || null })
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const updated = await res.json();
      setSelectedConv(updated);
      setConversations(prev => prev.map(c => c._id === updated._id ? updated : c));
    } catch (err) {
      console.error('Update assignee error:', err);
    }
  };

  // Toggle per-conversation AI opt-out. When disabled, the suggestion bar is
  // cleared and new inbound messages won't trigger /api/ai/suggest for this chat.
  const toggleChatAi = async () => {
    if (!selectedConv) return;
    const nextDisabled = !selectedConv.aiDisabled;
    try {
      const res = await inboxFetch(`/api/conversations/${selectedConv._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aiDisabled: nextDisabled })
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const updated = await res.json();
      setSelectedConv(updated);
      setConversations(prev => prev.map(c => c._id === updated._id ? updated : c));
      if (nextDisabled) {
        setAiSuggestion('');
        setAiType('recommendation');
        clearTimeout(aiDebounceRef.current);
      } else if (aiEnabled) {
        fetchAiSuggestion(updated._id);
      }
    } catch (err) {
      console.error('Toggle AI error:', err);
      alert('Could not update AI setting for this chat. Please try again.');
    }
  };

  // RUBBER-TIER ONLY: Permanently wipe the selected chat (messages + media + conv doc)
  // from the database and disk. Two-step confirm so this can't fire on an accidental
  // click. The actual UI removal is driven by the socket event so every connected
  // agent's inbox updates at the same time — no optimistic state changes here.
  const wipeChat = async () => {
    if (!selectedConv || !isRubber) return;
    const who = selectedConv.contactName || selectedConv.phone || 'this chat';
    const first = window.confirm(
      `Permanently wipe chat with ${who}?\n\n` +
      `This deletes every message and every attached media file from the database and the server.\n` +
      `This CANNOT be undone.`
    );
    if (!first) return;
    const typed = window.prompt('Type WIPE (uppercase) to confirm permanent deletion:');
    if (typed !== 'WIPE') {
      if (typed !== null) alert('Wipe cancelled — you did not type WIPE.');
      return;
    }
    try {
      const res = await inboxFetch(`/api/conversations/${selectedConv._id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server returned ${res.status}`);
      }
      // UI is cleared by the 'conversation_deleted' socket handler — this
      // client receives it too so we don't duplicate state changes here.
    } catch (err) {
      console.error('Wipe chat error:', err);
      alert(`Could not wipe chat: ${err.message}`);
    }
  };

  const handleSetPanel = (panel) => {
    selectedConvIdRef.current = null;
    setActivePanel(panel);
    setSelectedConv(null);
  };

  // Compute 24-hour window status from loaded messages
  const lastInboundMs = !loadingMsgs && messages.length > 0
    ? messages
        .filter(m => m.direction === 'inbound')
        .reduce((latest, m) => Math.max(latest, new Date(m.createdAt).getTime()), 0)
    : 0;

  const windowInfo = selectedConv && !loadingMsgs
    ? lastInboundMs > 0
      ? (() => {
          const timeLeftMs = 24 * 60 * 60 * 1000 - (Date.now() - lastInboundMs);
          return timeLeftMs > 0
            ? { outside: false, label: formatTimeLeft(timeLeftMs) }
            : { outside: true, label: '' };
        })()
      : null
    : null;

  const isOutside24h = windowInfo ? windowInfo.outside : false;

  return (
    <div style={s.layout}>
      {error && (
        <div style={s.errorBanner}>
          ⚠️ {error}
          <button style={s.retryBtn} onClick={loadConversations}>Retry</button>
        </div>
      )}
      <div style={{ ...s.columns, position: 'relative' }}>
        {/* Mobile backdrop — closes any open sidebar when tapped */}
        {isMobile && (leftOpen || rightOpen) && (
          <div
            style={s.mobileBackdrop}
            onClick={() => { setLeftOpen(false); setRightOpen(false); }}
          />
        )}

        <ConversationList
          conversations={conversations}
          selectedId={selectedConv?._id}
          onSelect={selectConversation}
          loading={loadingConvs}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={() => { if (!loadingMore && hasMore) loadConversations(page + 1); }}
          statusFilter={statusFilter}
          onStatusFilter={filter => { setStatusFilter(filter); setPage(1); }}
          isRubber={isRubber}
          settingsOpen={settingsOpen}
          onToggleSettings={() => setSettingsOpen(v => !v)}
          activePanel={activePanel}
          onSetPanel={handleSetPanel}
          isMobile={isMobile}
          isOpen={leftOpen}
          onClose={() => setLeftOpen(false)}
          aiPendingCount={aiPendingCount}
        />

        {activePanel ? (
          <SettingsPanel
            activePanel={activePanel}
            onClose={() => { setActivePanel(null); setSettingsOpen(false); }}
            onAiPendingCountChange={setAiPendingCount}
            user={user}
          />
        ) : (
          <>
            <ChatPanel
              conversation={selectedConv}
              messages={loadingMsgs ? [] : messages}
              onSendMessage={sendMessage}
              onSendNote={sendNote}
              onSendMedia={sendMedia}
              sending={sending}
              isMobile={isMobile}
              onToggleLeft={() => { setLeftOpen(v => !v); setRightOpen(false); }}
              onToggleRight={() => { setRightOpen(v => !v); setLeftOpen(false); }}
              isOutside24h={isOutside24h}
              aiSuggestion={aiSuggestion}
              aiType={aiType}
              aiLoading={aiLoading}
              aiError={aiError}
              aiGloballyEnabled={aiEnabled}
              onToggleChatAi={toggleChatAi}
              onDismissAi={() => { setAiSuggestion(''); setAiType('recommendation'); setAiError(''); }}
              onRefreshAi={() => { setAiSuggestion(''); setAiType('recommendation'); setAiError(''); fetchAiSuggestion(selectedConv?._id); }}
              candidResponses={candidResponses}
            />
            <ContactDetail
              conversation={selectedConv}
              onUpdateStatus={updateStatus}
              onUpdateAssignee={updateAssignee}
              isMobile={isMobile}
              isOpen={rightOpen}
              onClose={() => setRightOpen(false)}
              inboxAgents={inboxAgents}
              windowInfo={windowInfo}
              isRubber={isRubber}
              onWipeChat={wipeChat}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Exported wrapper with error boundary ─────────────────────────────────────
export default function SupportInbox() {
  return (
    <InboxErrorBoundary>
      <SupportInboxInner />
    </InboxErrorBoundary>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;
const DOWNLOAD_EXT = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|zip|rar|tar|gz|7z|exe|dmg|apk|msi|deb|rpm|mp3|mp4|avi|mov|mkv|flv|wmv|wav|aac|ogg|flac)(\?[^\s]*)?$/i;

function linkifyText(text, isOutbound) {
  if (!text) return text;
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) => {
    if (/^https?:\/\//i.test(part)) {
      if (DOWNLOAD_EXT.test(part)) return part; // don't link file downloads
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: isOutbound ? 'rgba(255,255,255,0.9)' : colors.blue,
            textDecoration: 'underline',
            wordBreak: 'break-all'
          }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

// ─── Styles ───────────────────────────────────────────────────────────────────
// Inbox chrome surfaces now reference the flipping theme tokens.
const SURFACE = colors.surfaceSolid;       // panels (sidebar, header, input, detail)
const SURFACE_DEEP = colors.surfaceDeep;   // chat/messages backdrop + headers
const INBOUND = colors.surface2;           // received message bubble
const BORDER = colors.glassBorder;
const BORDER_STRONG = colors.glassBorderStrong;
const TXT = colors.text;
const TXT_MUTED = colors.textMuted;
const TXT_FAINT = colors.textFaint;
const GREEN = colors.parGreen;
const GREEN_BRIGHT = colors.parGreenBright;

const s = {
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 50px)',
    overflow: 'hidden',
    background: SURFACE_DEEP
  },
  errorBanner: {
    background: 'rgba(229,84,75,0.14)',
    borderBottom: '1px solid rgba(229,84,75,0.35)',
    color: '#f3b0aa',
    padding: '8px 16px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  retryBtn: {
    background: colors.red,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '3px 10px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  columns: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  // Mobile
  mobileBackdrop: {
    position: 'absolute',
    inset: 0,
    background: colors.overlay,
    zIndex: 199
  },
  mobileIconBtn: {
    background: 'transparent',
    border: 'none',
    color: TXT,
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
    flexShrink: 0
  },
  chatAiToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 10px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
    border: '1px solid transparent',
    transition: 'background 0.15s, border-color 0.15s, opacity 0.15s',
    userSelect: 'none'
  },
  chatAiToggleOn: {
    background: 'rgba(47,182,92,0.16)',
    borderColor: 'rgba(47,182,92,0.45)',
    color: GREEN_BRIGHT
  },
  chatAiToggleOff: {
    background: 'rgba(229,84,75,0.14)',
    borderColor: 'rgba(229,84,75,0.4)',
    color: '#f3b0aa'
  },
  chatAiToggleDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },
  chatAiIcon: {
    fontSize: '14px',
    lineHeight: 1
  },
  chatAiLabel: {
    letterSpacing: '0.02em'
  },
  mobileMenuBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: colors.surface2,
    color: TXT,
    border: `1px solid ${BORDER}`,
    borderRadius: '999px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '16px'
  },
  sidebarCloseBtn: {
    background: 'transparent',
    border: 'none',
    color: TXT_MUTED,
    fontSize: '16px',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  // Sidebar
  sidebar: {
    width: '300px',
    minWidth: '260px',
    background: SURFACE,
    borderRight: `1px solid ${BORDER}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  sidebarHeader: {
    padding: '12px',
    borderBottom: `1px solid ${BORDER}`,
    background: SURFACE_DEEP
  },
  sidebarTitle: {
    color: TXT,
    fontWeight: '700',
    fontSize: '15px',
    display: 'block',
    marginBottom: '8px'
  },
  statusFilters: {
    display: 'flex',
    gap: '4px'
  },
  filterBtn: {
    padding: '3px 8px',
    border: `1px solid ${BORDER_STRONG}`,
    background: SURFACE_DEEP,
    color: TXT_MUTED,
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600'
  },
  filterBtnActive: {
    background: 'rgba(47,182,92,0.20)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: GREEN_BRIGHT
  },
  loadingText: {
    padding: '20px',
    textAlign: 'center',
    color: TXT_MUTED,
    fontSize: '13px'
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: TXT_MUTED,
    fontSize: '13px'
  },
  conversationList: {
    flex: 1,
    overflowY: 'auto'
  },
  convItem: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '12px',
    gap: '10px',
    borderBottom: `1px solid ${BORDER}`,
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  convItemActive: {
    background: 'rgba(47,182,92,0.14)'
  },
  convAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: GREEN,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '16px',
    flexShrink: 0
  },
  convInfo: {
    flex: 1,
    minWidth: 0
  },
  convTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  convName: {
    fontWeight: '600',
    fontSize: '13px',
    color: TXT,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '140px'
  },
  convTime: {
    fontSize: '11px',
    color: TXT_FAINT,
    flexShrink: 0
  },
  convBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '2px'
  },
  convLastMsg: {
    fontSize: '12px',
    color: TXT_MUTED,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '160px'
  },
  unreadBadge: {
    background: GREEN,
    color: 'white',
    borderRadius: '10px',
    fontSize: '10px',
    padding: '1px 6px',
    fontWeight: '700',
    flexShrink: 0
  },
  convMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '3px',
    flexWrap: 'wrap'
  },
  statusDot: {
    fontSize: '10px',
    padding: '1px 6px',
    borderRadius: '8px',
    color: 'white',
    fontWeight: '600',
    display: 'inline-block'
  },
  assigneeBadge: {
    fontSize: '10px',
    padding: '1px 6px',
    borderRadius: '8px',
    background: 'rgba(58,155,212,0.20)',
    color: '#8fcdee',
    fontWeight: '600',
    display: 'inline-block'
  },
  // Sidebar footer / settings
  sidebarFooter: {
    borderTop: `1px solid ${BORDER}`,
    padding: '8px'
  },
  gearBtn: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    background: colors.surface,
    color: TXT,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  gearBtnActive: {
    background: 'rgba(47,182,92,0.18)',
    borderColor: 'rgba(47,182,92,0.5)',
    color: GREEN_BRIGHT
  },
  wipeZone: {
    border: '1px solid rgba(229,84,75,0.35)',
    background: 'rgba(229,84,75,0.10)',
    borderRadius: '8px',
    padding: '10px',
    marginTop: '4px'
  },
  wipeZoneLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#f3b0aa',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '8px'
  },
  wipeBtn: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.redDeep}`,
    borderRadius: '8px',
    background: colors.red,
    color: 'white',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '700',
    textAlign: 'center'
  },
  wipeZoneHint: {
    fontSize: '11px',
    color: 'rgba(243,176,170,0.8)',
    marginTop: '8px',
    lineHeight: '1.4'
  },
  settingsNav: {
    marginTop: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  settingsNavBtn: {
    padding: '7px 10px',
    border: 'none',
    borderRadius: '8px',
    background: 'transparent',
    color: TXT,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    textAlign: 'left'
  },
  settingsNavBtnActive: {
    background: 'rgba(47,182,92,0.16)',
    color: GREEN_BRIGHT
  },
  // Chat panel
  chatEmpty: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: SURFACE_DEEP
  },
  chatPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: SURFACE_DEEP
  },
  chatHeader: {
    padding: '12px 16px',
    background: SURFACE,
    borderBottom: `1px solid ${BORDER}`,
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  chatAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: GREEN,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '15px'
  },
  chatName: {
    fontWeight: '600',
    fontSize: '14px',
    color: TXT
  },
  chatPhone: {
    fontSize: '12px',
    color: TXT_MUTED
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  noMessages: {
    textAlign: 'center',
    color: TXT_MUTED,
    fontSize: '13px',
    marginTop: '40px'
  },
  messageBubble: {
    maxWidth: '70%',
    padding: '8px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    lineHeight: '1.5'
  },
  inbound: {
    alignSelf: 'flex-start',
    background: INBOUND,
    color: TXT,
    borderRadius: '0 12px 12px 12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.25)'
  },
  outbound: {
    alignSelf: 'flex-end',
    background: GREEN,
    color: 'white',
    borderRadius: '12px 0 12px 12px'
  },
  outboundAi: {
    alignSelf: 'flex-end',
    background: colors.blue,
    color: 'white',
    borderRadius: '12px 0 12px 12px',
    boxShadow: '0 1px 3px rgba(58, 155, 212, 0.3)'
  },
  agentLabel: {
    fontSize: '10px',
    opacity: 0.85,
    marginBottom: '2px',
    fontWeight: '600'
  },
  noteBubble: {
    alignSelf: 'center',
    background: 'rgba(240,168,48,0.16)',
    color: '#f0c68a',
    borderRadius: '8px',
    borderLeft: `4px solid ${colors.amber}`,
    maxWidth: '80%'
  },
  noteLabel: {
    fontSize: '10px',
    fontWeight: '700',
    fontStyle: 'italic',
    color: '#f0c68a',
    marginBottom: '4px'
  },
  messageText: {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  messageTime: {
    fontSize: '10px',
    opacity: 0.7,
    marginTop: '4px',
    textAlign: 'right'
  },
  inputWrapper: {
    background: SURFACE,
    borderTop: `1px solid ${BORDER}`
  },
  modeToggle: {
    display: 'flex',
    padding: '8px 12px 0',
    gap: '6px'
  },
  modeBtn: {
    padding: '4px 12px',
    border: `1px solid ${BORDER}`,
    borderRadius: '12px',
    background: SURFACE_DEEP,
    color: TXT_MUTED,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    transition: 'all 0.15s'
  },
  modeBtnActive: {
    background: 'rgba(47,182,92,0.20)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: GREEN_BRIGHT
  },
  modeNoteActive: {
    background: 'rgba(240,168,48,0.20)',
    borderColor: 'rgba(240,168,48,0.55)',
    color: '#f0c68a'
  },
  // Wrapper needs relative positioning so the absolute-positioned menu
  // anchors to the button, not the whole chat panel.
  candidWrap: {
    position: 'relative',
    marginLeft: 'auto' // push the candid button to the right of Reply/Note
  },
  candidMenu: {
    position: 'absolute',
    right: 0,
    bottom: '100%',
    marginBottom: '6px',
    minWidth: '260px',
    maxWidth: '340px',
    maxHeight: '320px',
    overflowY: 'auto',
    background: SURFACE_DEEP,
    border: `1px solid ${BORDER}`,
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    padding: '6px',
    zIndex: 30
  },
  candidEmpty: {
    padding: '14px 12px',
    fontSize: '12px',
    color: TXT_MUTED,
    textAlign: 'center',
    lineHeight: 1.5
  },
  candidItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gap: '3px',
    padding: '8px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left'
  },
  candidItemTitle: {
    fontSize: '13px',
    fontWeight: 700,
    color: TXT
  },
  candidItemPreview: {
    fontSize: '11px',
    color: TXT_MUTED,
    lineHeight: 1.35,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  inputRow: {
    display: 'flex',
    padding: '8px 12px 12px',
    gap: '8px',
    alignItems: 'flex-end'
  },
  inputRowNote: {
    background: 'rgba(240,168,48,0.07)'
  },
  textarea: {
    flex: 1,
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '13px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.5',
    background: colors.inputBg,
    color: TXT
  },
  sendBtn: {
    background: GREEN,
    color: 'white',
    border: 'none',
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  sendBtnNote: {
    background: colors.amber
  },
  sendBtnDisabled: {
    background: colors.surface2,
    color: TXT_FAINT,
    cursor: 'not-allowed'
  },
  // Contact detail
  detailEmpty: {
    width: '220px',
    background: SURFACE,
    borderLeft: `1px solid ${BORDER}`
  },
  detailPanel: {
    width: '220px',
    minWidth: '200px',
    background: SURFACE,
    borderLeft: `1px solid ${BORDER}`,
    padding: '16px',
    overflowY: 'auto',
    color: TXT
  },
  detailHeader: {
    fontWeight: '700',
    fontSize: '13px',
    color: TXT,
    marginBottom: '0'
  },
  detailAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: GREEN,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '22px',
    margin: '0 auto 8px'
  },
  detailName: {
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '14px',
    color: TXT
  },
  detailPhone: {
    textAlign: 'center',
    fontSize: '12px',
    color: TXT_MUTED,
    marginTop: '2px'
  },
  detailDivider: {
    height: '1px',
    background: BORDER,
    margin: '12px 0'
  },
  detailLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: TXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px'
  },
  detailValue: {
    fontSize: '13px',
    color: TXT
  },
  statusButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  statusBtn: {
    padding: '6px 10px',
    border: `1px solid ${BORDER}`,
    background: SURFACE_DEEP,
    borderRadius: '999px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    color: TXT,
    textAlign: 'left',
    transition: 'all 0.15s'
  },
  statusBtnActive: {
    background: 'rgba(47,182,92,0.20)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: GREEN_BRIGHT
  },
  assigneeSelect: {
    width: '100%',
    padding: '6px 8px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    fontSize: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    background: SURFACE_DEEP,
    color: TXT,
    cursor: 'pointer'
  },
  windowBadge: {
    fontSize: '11px',
    fontWeight: '600',
    padding: '6px 10px',
    borderRadius: '8px',
    marginBottom: '2px',
    lineHeight: '1.4'
  },
  windowBadgeActive: {
    background: 'rgba(47,182,92,0.16)',
    color: GREEN_BRIGHT,
    border: '1px solid rgba(47,182,92,0.35)'
  },
  windowBadgeExpired: {
    background: 'rgba(229,84,75,0.14)',
    color: '#f3b0aa',
    border: '1px solid rgba(229,84,75,0.35)'
  },
  windowBlockedBar: {
    padding: '8px 12px',
    background: 'rgba(240,168,48,0.12)',
    borderBottom: '1px solid rgba(240,168,48,0.35)',
    color: '#f0c68a',
    fontSize: '11px',
    fontWeight: '600'
  },
  attachBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 4px',
    flexShrink: 0,
    opacity: 0.7,
    lineHeight: 1
  },
  // Wraps the thumbnail-bar and the caption row so the whole "adding a photo"
  // area shares one green backdrop, WhatsApp-style.
  attachPreviewWrap: {
    background: 'rgba(47,182,92,0.12)',
    borderBottom: '1px solid rgba(47,182,92,0.3)'
  },
  attachPreviewBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    gap: '8px'
  },
  captionRow: {
    display: 'flex',
    alignItems: 'flex-end',
    padding: '4px 12px 10px',
    gap: '8px'
  },
  captionInput: {
    flex: 1,
    padding: '8px 10px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: 1.4,
    resize: 'none',
    outline: 'none',
    boxSizing: 'border-box',
    background: SURFACE_DEEP,
    color: TXT
  },
  captionUnsupported: {
    flex: 1,
    padding: '10px 12px',
    fontSize: '12px',
    fontStyle: 'italic',
    color: TXT_MUTED
  },
  attachThumb: {
    width: '52px',
    height: '52px',
    objectFit: 'cover',
    borderRadius: '6px',
    flexShrink: 0
  },
  attachFileIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '24px',
    flexShrink: 0,
    color: TXT
  },
  attachCancelBtn: {
    background: 'transparent',
    border: `1px solid ${BORDER}`,
    borderRadius: '4px',
    padding: '2px 8px',
    cursor: 'pointer',
    fontSize: '12px',
    color: TXT_MUTED,
    flexShrink: 0
  },
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(47, 182, 92, 0.16)',
    border: `3px dashed ${GREEN}`,
    borderRadius: '8px',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    backdropFilter: 'blur(1px)'
  },
  dropOverlayInner: {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    padding: '24px 32px',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
    textAlign: 'center'
  },
  dropIcon: {
    fontSize: '42px',
    marginBottom: '6px'
  },
  dropTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: GREEN_BRIGHT,
    marginBottom: '4px'
  },
  dropHint: {
    fontSize: '12px',
    color: TXT_MUTED
  },
  inputRowBlocked: {
    background: colors.surfaceSoft
  },
  textareaBlocked: {
    background: colors.surfaceSoft,
    color: TXT_FAINT,
    cursor: 'not-allowed'
  },
  // AI badge on nav button
  aiBadge: {
    background: GREEN,
    color: 'white',
    borderRadius: '10px',
    fontSize: '9px',
    padding: '1px 5px',
    fontWeight: '700',
    lineHeight: '1.4'
  },
  // AI suggestion bar — base
  aiBar: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    background: 'rgba(47,182,92,0.12)',
    borderTop: '1px solid rgba(47,182,92,0.3)',
    borderBottom: '1px solid rgba(47,182,92,0.3)'
  },
  // Priming (need more info) — blue tint
  aiBarPriming: {
    background: 'rgba(58,155,212,0.12)',
    borderTopColor: 'rgba(58,155,212,0.3)',
    borderBottomColor: 'rgba(58,155,212,0.3)'
  },
  // Recommendation (product found) — green tint (default)
  aiBarRecommendation: {
    background: 'rgba(47,182,92,0.12)',
    borderTopColor: 'rgba(47,182,92,0.3)',
    borderBottomColor: 'rgba(47,182,92,0.3)'
  },
  aiBarError: {
    background: 'rgba(229,84,75,0.12)',
    borderTopColor: 'rgba(229,84,75,0.35)',
    borderBottomColor: 'rgba(229,84,75,0.35)'
  },
  aiBarIcon: {
    fontSize: '18px',
    flexShrink: 0,
    marginTop: '2px'
  },
  aiBarBody: {
    flex: 1,
    minWidth: 0
  },
  aiBarLabel: {
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '3px'
  },
  aiBarLabelPriming: { color: '#8fcdee' },
  aiBarLabelRecommendation: { color: GREEN_BRIGHT },
  aiBarText: {
    fontSize: '12px',
    color: TXT,
    lineHeight: '1.5',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  aiBarActions: {
    display: 'flex',
    gap: '6px',
    marginTop: '7px'
  },
  aiUseBtn: {
    background: GREEN,
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600'
  },
  // Priming "use" button is blue instead of green
  aiUseBtnPriming: {
    background: colors.blue
  },
  aiRefreshBtn: {
    background: 'transparent',
    color: TXT_MUTED,
    border: `1px solid ${BORDER}`,
    borderRadius: '6px',
    padding: '4px 10px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600'
  },
  aiDismissBtn: {
    background: 'transparent',
    border: 'none',
    color: TXT_MUTED,
    cursor: 'pointer',
    fontSize: '14px',
    padding: '0 2px',
    flexShrink: 0,
    lineHeight: 1,
    marginTop: '2px'
  },
  // Settings panel
  settingsPanelWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: SURFACE_DEEP,
    overflow: 'hidden'
  },
  settingsPanelHeader: {
    padding: '12px 20px',
    background: SURFACE,
    borderBottom: `1px solid ${BORDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  settingsPanelTitle: {
    fontWeight: '700',
    fontSize: '15px',
    color: TXT
  },
  settingsCloseBtn: {
    padding: '5px 12px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    background: colors.surface,
    color: TXT,
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },
  settingsPanelBody: {
    flex: 1,
    overflowY: 'auto'
  }
};
